import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/select";
import { intVN } from "@/features/dashboard/lib/format";
import { useIsMobile } from "@/hooks/useIsMobile";

export type PaginationProps = {
  total: number;
  pageSize: number;
  current: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  itemLabel?: string;
  className?: string;
};

function compactPages(current: number, totalPages: number): (number | "…")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const out: (number | "…")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(totalPages - 1, current + 1);
  if (start > 2) out.push("…");
  for (let p = start; p <= end; p++) out.push(p);
  if (end < totalPages - 1) out.push("…");
  out.push(totalPages);
  return out;
}

export function Pagination({
  total,
  pageSize,
  current,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  itemLabel = "kết quả",
  className,
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safeCurrent = Math.min(Math.max(1, current), totalPages);
  const pages = compactPages(safeCurrent, totalPages);
  const isMobile = useIsMobile();

  const from = total === 0 ? 0 : (safeCurrent - 1) * pageSize + 1;
  const to = Math.min(safeCurrent * pageSize, total);

  return (
    <div
      className={
        "flex flex-wrap items-center justify-between gap-3 border-t border-border px-3 py-2 text-sm " +
        (className ?? "")
      }
    >
      <div className="text-muted-foreground">
        {total === 0 ? (
          <span>Không có {itemLabel}</span>
        ) : (
          <>
            <span className="font-medium text-foreground">{intVN(from)}–{intVN(to)}</span>
            <span> / </span>
            <span className="font-medium text-foreground">{intVN(total)}</span>
            <span className="hidden sm:inline"> {itemLabel}</span>
          </>
        )}
      </div>

      <div className="flex items-center gap-2">
        {onPageSizeChange && !isMobile && (
          <Select
            value={String(pageSize)}
            onValueChange={(v) => onPageSizeChange(Number(v))}
          >
            <SelectTrigger className="h-8 w-[88px] text-xs" aria-label="Số dòng / trang">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((opt) => (
                <SelectItem key={opt} value={String(opt)}>
                  {opt} / trang
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={safeCurrent <= 1}
            onClick={() => onPageChange(safeCurrent - 1)}
            aria-label="Trang trước"
          >
            <ChevronLeft />
          </Button>

          {isMobile ? (
            <span className="px-2 text-xs text-muted-foreground">
              {safeCurrent}/{totalPages}
            </span>
          ) : (
            pages.map((p, i) =>
              p === "…" ? (
                <span key={`gap-${i}`} className="px-2 text-muted-foreground">…</span>
              ) : (
                <Button
                  key={p}
                  variant={p === safeCurrent ? "default" : "outline"}
                  size="sm"
                  className="h-8 min-w-[32px] px-2"
                  onClick={() => onPageChange(p)}
                  aria-current={p === safeCurrent ? "page" : undefined}
                >
                  {p}
                </Button>
              ),
            )
          )}

          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={safeCurrent >= totalPages}
            onClick={() => onPageChange(safeCurrent + 1)}
            aria-label="Trang sau"
          >
            <ChevronRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
